import { useChatContext } from '@/chat/ChatContext';
import { ProductCard } from '@/components/ProductCard';
import { ChipAwareMarkdown } from '@/markdown/chips';
import { SectionContainer } from './SectionContainer';
import type { ProductRecommendationsSection, Section } from '@/types/chat';

/**
 * 后端把「单个产品卡 → 紧跟一段解释它的 direct_answer」交错输出。把那段
 * 解释的第一段挂到产品 section 上，由卡片自己显示；其余段落照常渲染。
 * 只有第一段确实链接到这个产品（`slug)` 链接或 `slug]` chip）才配对。
 */
export function attachExplanations(sections: Section[]): Section[] {
  const out: Section[] = [];
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    const next = sections[i + 1];
    if (
      section?.type === 'product_recommendations' &&
      Array.isArray(section.skus) &&
      section.skus.length === 1 &&
      next?.type === 'direct_answer' &&
      !next.title &&
      typeof next.body_md === 'string'
    ) {
      const slug = section.skus[0];
      const [para, ...rest] = next.body_md.split(/\n{2,}/);
      if (para.includes(`${slug})`) || para.includes(`${slug}]`)) {
        out.push({ ...section, explanation: para });
        if (rest.length > 0) out.push({ ...next, body_md: rest.join('\n\n') });
        i++;
        continue;
      }
    }
    out.push(section);
  }
  return out;
}

export function ProductRecommendationsView({
  section,
}: {
  section: ProductRecommendationsSection;
}): JSX.Element | null {
  const { payload } = useChatContext();
  // section.skus[] values are slugs (field name is misleading per doc §6.1).
  // Default to [] so streaming partials and any malformed final responses
  // can't crash the React tree.
  const skus = Array.isArray(section.skus) ? section.skus : [];
  const products = skus
    .map((slug) => payload.products.find((p) => p.slug === slug))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));
  const { explanation } = section;

  // 产品对象解析不出来时（老 session 快照、流式 partial）仍然渲染标题和
  // 引导语——整块消失会让回复读起来缺一截。
  if (products.length === 0 && !section.title && !section.intro_md && !explanation) return null;

  return (
    <section className="cal-precs tw-space-y-3">
      {section.title && (
        <h3 className="tw-font-serif tw-text-[15.5px] tw-font-bold tw-text-cal-ink">
          {section.title}
        </h3>
      )}
      {section.intro_md && (
        <ChipAwareMarkdown
          source={section.intro_md}
          className="tw-text-[14px] tw-text-cal-ink-soft tw-leading-relaxed"
        />
      )}
      {products.length > 0 && (
        <div className="cal-precs-grid tw-grid tw-grid-cols-1 tw-gap-3">
          {/* explanation is only ever set on single-product sections */}
          {products.map((product) => (
            <ProductCard key={product.slug} product={product} explanation={explanation} />
          ))}
        </div>
      )}
      {/* 卡片解析不出来时，解释就是普通回复文字，和配对前一样渲染。 */}
      {explanation && products.length === 0 && <SectionContainer body={explanation} />}
    </section>
  );
}
